import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { getDecoFlowMetric } from '../deco-flow.models';
import {
    FlowDashboardData,
    getFlowAxis,
} from 'src/app/services/flow-dashboard/flow-dashboard.models';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardDecoActivitySqlMap } from '../../mopa-resources-detailed/sql-maps/biviz-dashboard-deco-activity-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

/**
 * Deco Flow by-date loader. Deco Flow follows Resource Flow's daily calculation model on decoration
 * activity data: on `dashboard_deco_activity` the inflow side is `inflow_delta` (decorations gained)
 * and the outflow side is `new_placed_delta` (decorations placed).
 *
 * Every metric mode states its own complete query so the emitted SQL reads as SQL. Compiled Split /
 * Group / filter fragments are interpolated where they belong in the statement instead of being
 * pre-assembled into JS string variables — same convention as
 * `mopa-resources-detailed/data-loaders/deco-activity-by-date.ts`.
 */

export interface DecoFlowByDateDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

export class DecoFlowByDateDataLoaderFactory extends Factory<
    DataLoader,
    DecoFlowByDateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS = 'DecoFlowByDateDataLoaderFactory';

    getInstance(options: DecoFlowByDateDataLoaderFactoryOptions): DataLoader {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                if (getFlowAxis(queryContext) !== 'date') {
                    return;
                }

                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const decoSqlMap = BiVizDashboardDecoActivitySqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    decoSqlMap.columns
                );
                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const decoSQL =
                    BiVizDashboardDecoActivitySqlMap.sqlStatementsCompiler.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const metric = getDecoFlowMetric(queryContext);

                if (
                    metric.statistic === 'percentile' &&
                    !metric.percentiles.length
                ) {
                    throw new Error(
                        'Deco Flow: percentile mode needs at least one selected percentile'
                    );
                }

                const splitsByPercentile =
                    metric.statistic === 'percentile' &&
                    metric.percentiles.length > 1;
                // Average and Percentile both offer the DAU population, and both then need the
                // users-activity table.
                const usesDauPopulation =
                    (metric.statistic === 'average' ||
                        metric.statistic === 'percentile') &&
                    metric.population === 'dau';

                const usersdashActivity = BiVizDashboardUsersActivity;
                const usersdashActivityDsColumns = usesDauPopulation
                    ? DataLoader.separatePresentAndMissingDims(
                          queryContext.datasourceColumns,
                          usersdashActivity.sqlMap.columns
                      )
                    : undefined;
                const usersdashActivitySQL = usersdashActivityDsColumns
                    ? usersdashActivity.compileSQLStatements(
                          usersdashActivityDsColumns.present,
                          queryContext.settings,
                          options
                      )
                    : undefined;

                const decoTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    decoSqlMap.dataset,
                    decoSqlMap.tableId
                );
                const usersdashActivityTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    usersdashActivity.sqlMap.dataset,
                    usersdashActivity.sqlMap.tableId
                );

                const buildQuery = (): string => {
                    if (metric.statistic === 'total') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow - mode: total

WITH base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ds.date
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)

SELECT
    ${decoSQL.splitGroupBy}${
        decoSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , ROUND(SUM(inflow_delta), 2)     AS inflow
  , ROUND(SUM(new_placed_delta), 2) AS outflow
FROM base
GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
ORDER BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
`;
                    }

                    if (metric.statistic === 'flow records') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow - mode: flow records

WITH base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ds.date
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)

SELECT
    ${decoSQL.splitGroupBy}${
        decoSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , COUNTIF(inflow_delta > 0)     AS inflow
  , COUNTIF(new_placed_delta > 0) AS outflow
FROM base
GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
ORDER BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
`;
                    }

                    if (metric.statistic === 'players') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow - mode: players

WITH base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ds.date
    , ds.player_id
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)

SELECT
    ${decoSQL.splitGroupBy}${
        decoSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , COUNT(DISTINCT IF(inflow_delta > 0, player_id, NULL))     AS inflow
  , COUNT(DISTINCT IF(new_placed_delta > 0, player_id, NULL)) AS outflow
FROM base
GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
ORDER BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
`;
                    }

                    if (
                        metric.statistic === 'average' &&
                        metric.population === 'deu'
                    ) {
                        // `deu` divides by the players with a positive flow on that side, so each side
                        // gets its own denominator.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow - mode: average per deu

WITH base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ds.date
    , ds.player_id
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)

SELECT
    ${decoSQL.splitGroupBy}${
        decoSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , ROUND(SAFE_DIVIDE(
        SUM(inflow_delta),
        COUNT(DISTINCT IF(inflow_delta > 0, player_id, NULL))
    ), 2) AS inflow
  , ROUND(SAFE_DIVIDE(
        SUM(new_placed_delta),
        COUNT(DISTINCT IF(new_placed_delta > 0, player_id, NULL))
    ), 2) AS outflow
FROM base
GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
ORDER BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
`;
                    }

                    if (
                        metric.statistic === 'average' &&
                        metric.population === 'dau'
                    ) {
                        // The DAU denominator follows cohort splits; a datasource-specific split and a
                        // Group/Bucket only re-partition the same population, so they are cross-joined
                        // onto it instead of narrowing it.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow - mode: average per dau

WITH base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ${decoSQL.datasourceSpecificSplitSelect}
    ${decoSQL.cohortSplitSelect}
    ds.date
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)
, decoAgg AS
(
  SELECT ${decoSQL.splitGroupBy}${
      decoSQL.bucketGroupBy
  }${decoSQL.dateTrunc()} AS date
    , SUM(inflow_delta)     AS sum_inflow
    , SUM(new_placed_delta) AS sum_outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
)
, active_users AS
(
  SELECT
    ${decoSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    date
    , sum_daily_active_users
  FROM
  (
    SELECT
      ${decoSQL.cohortSplitGroupBy}${decoSQL.dateTrunc()} AS date
      , SUM(daily_active_users) AS sum_daily_active_users
    FROM
    (
      SELECT
        ${
            usersdashActivitySQL?.splitGroupBy
                ? usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )
                : ''
        }date
        , COUNT(DISTINCT player_id) AS daily_active_users
      FROM \`${usersdashActivityTablePath}\` ds
      ${usersdashActivitySQL?.join ?? ''}
      ${usersdashActivitySQL?.where ?? ''}
      GROUP BY ${decoSQL.cohortSplitGroupBy}date
    )
    GROUP BY ${decoSQL.cohortSplitGroupBy}date
  )
  ${
      decoSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${decoSQL.datasourceSpecificSplitGroupAlias} FROM base)`
          : ''
  }
)

SELECT
    ${decoSQL.splitGroup ? 'decoAgg.split_group AS split_group, ' : ''}${
        decoSQL.bucketGroup ? 'decoAgg.bucket_group AS bucket_group, ' : ''
    }FORMAT_DATE('%Y-%m-%d', DATE(decoAgg.date)) AS date
  , ROUND(SAFE_DIVIDE(decoAgg.sum_inflow, active_users.sum_daily_active_users), 2)  AS inflow
  , ROUND(SAFE_DIVIDE(decoAgg.sum_outflow, active_users.sum_daily_active_users), 2) AS outflow
FROM decoAgg
LEFT JOIN active_users
  ON decoAgg.date = active_users.date
  ${
      decoSQL.splitGroup
          ? 'AND decoAgg.split_group = active_users.split_group'
          : ''
  }
ORDER BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
`;
                    }

                    if (
                        metric.statistic === 'percentile' &&
                        metric.population === 'deu'
                    ) {
                        // A percentile describes one player, so rows are first summed per player and
                        // date bucket. `deu` keeps only the players with a positive flow on that side
                        // (APPROX_QUANTILES skips NULL), so the opposite side's players cannot drag the
                        // percentile to zero.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow - mode: ${metric.label}

WITH base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ds.date
    , ds.player_id
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)
, perPlayer AS
(
  SELECT ${decoSQL.splitGroupBy}${
      decoSQL.bucketGroupBy
  }${decoSQL.dateTrunc()} AS date
    , player_id
    , SUM(inflow_delta)     AS inflow
    , SUM(new_placed_delta) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date, player_id
)
, decoAgg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date${metric.percentiles
      .map(
          (percentile) => `
    , APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${percentile})]   AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
      )
      .join('')}
  FROM perPlayer
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
)

SELECT
    ${decoSQL.splitGroupBy}${
        decoSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(date)) AS date
  , quantile_inflow_${metric.percentiles[0]}  AS inflow
  , quantile_outflow_${metric.percentiles[0]} AS outflow${
      splitsByPercentile
          ? metric.percentiles
                .map(
                    (percentile) => `
  , quantile_inflow_${percentile}
  , quantile_outflow_${percentile}`
                )
                .join('')
          : ''
  }
FROM decoAgg
ORDER BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
`;
                    }

                    if (
                        metric.statistic === 'percentile' &&
                        metric.population === 'dau'
                    ) {
                        // `dau` percentiles every active player, reading a player without flow as zero,
                        // so the per-player values are first joined onto the whole active population.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow - mode: ${metric.label}

WITH base AS
(
  SELECT
    ${decoSQL.splitSelect}
    ${decoSQL.datasourceSpecificSplitSelect}
    ${decoSQL.cohortSplitSelect}
    ds.date
    , ds.player_id
    , ds.inflow_delta
    , ds.new_placed_delta
    ${decoSQL.bucketSelect ? `, ${decoSQL.bucketSelect}` : ''}
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
)
, perPlayer AS
(
  SELECT ${decoSQL.splitGroupBy}${
      decoSQL.bucketGroupBy
  }${decoSQL.dateTrunc()} AS date
    , player_id
    , SUM(inflow_delta)     AS inflow
    , SUM(new_placed_delta) AS outflow
  FROM base
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date, player_id
)
, dauPlayers AS
(
  SELECT
    ${decoSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    date
    , player_id
  FROM
  (
    SELECT ${decoSQL.cohortSplitGroupBy}${decoSQL.dateTrunc()} AS date
      , player_id
    FROM
    (
      SELECT
        ${
            usersdashActivitySQL?.splitGroupBy
                ? usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )
                : ''
        }date
        , player_id
      FROM \`${usersdashActivityTablePath}\` ds
      ${usersdashActivitySQL?.join ?? ''}
      ${usersdashActivitySQL?.where ?? ''}
      GROUP BY ${decoSQL.cohortSplitGroupBy}date, player_id
    )
    GROUP BY ${decoSQL.cohortSplitGroupBy}date, player_id
  )
  ${
      decoSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${decoSQL.datasourceSpecificSplitGroupAlias} FROM base)`
          : ''
  }
  ${
      decoSQL.bucketGroup
          ? 'CROSS JOIN (SELECT DISTINCT bucket_group FROM base)'
          : ''
  }
)
, population AS
(
  SELECT ${
      decoSQL.splitGroup ? 'dauPlayers.split_group AS split_group, ' : ''
  }${
      decoSQL.bucketGroup ? 'dauPlayers.bucket_group AS bucket_group, ' : ''
  }dauPlayers.date AS date
    , COALESCE(perPlayer.inflow, 0)  AS inflow
    , COALESCE(perPlayer.outflow, 0) AS outflow
  FROM dauPlayers
  LEFT JOIN perPlayer
    ON perPlayer.date = dauPlayers.date
   AND perPlayer.player_id = dauPlayers.player_id
   ${
       /* A Split or Group expression over a nullable column (e.g. `income_type` is NULL on
          placement rows) yields a NULL group, which plain equality would never match: the whole
          group would read as zero flow. */
       decoSQL.splitGroup
           ? `AND IFNULL(perPlayer.split_group, '_NULL_') = IFNULL(dauPlayers.split_group, '_NULL_')`
           : ''
   }
   ${
       decoSQL.bucketGroup
           ? `AND IFNULL(perPlayer.bucket_group, '_NULL_') = IFNULL(dauPlayers.bucket_group, '_NULL_')`
           : ''
   }
)
, decoAgg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date${metric.percentiles
      .map(
          (percentile) => `
    , APPROX_QUANTILES(inflow, 100)[OFFSET(${percentile})]  AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(outflow, 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
      )
      .join('')}
  FROM population
  GROUP BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
)

SELECT
    ${decoSQL.splitGroupBy}${
        decoSQL.bucketGroupBy
    }FORMAT_DATE('%Y-%m-%d', DATE(date)) AS date
  , quantile_inflow_${metric.percentiles[0]}  AS inflow
  , quantile_outflow_${metric.percentiles[0]} AS outflow${
      splitsByPercentile
          ? metric.percentiles
                .map(
                    (percentile) => `
  , quantile_inflow_${percentile}
  , quantile_outflow_${percentile}`
                )
                .join('')
          : ''
  }
FROM decoAgg
ORDER BY ${decoSQL.splitGroupBy}${decoSQL.bucketGroupBy}date
`;
                    }

                    // A new statistic or population must state its own query instead of silently
                    // inheriting another one.
                    throw new Error(
                        `Deco Flow: no query defined for statistic '${metric.statistic}' and population '${metric.population}'`
                    );
                };

                const query = buildQuery();
                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                const resultRows = [...queryResponse.result];
                const rows = splitsByPercentile
                    ? resultRows.flatMap((row) =>
                          metric.percentiles.map((percentile) => {
                              const label = `P${percentile}`;
                              return {
                                  ...row,
                                  inflow: row[`quantile_inflow_${percentile}`],
                                  outflow:
                                      row[`quantile_outflow_${percentile}`],
                                  split_group: decoSQL.splitGroup
                                      ? `${row.split_group ?? 'Total'} · ${label}`
                                      : label,
                                  ...(decoSQL.bucketGroup
                                      ? {
                                            bucket_group: `${
                                                row.bucket_group ?? 'Unknown'
                                            } · ${label}`,
                                        }
                                      : {}),
                              };
                          })
                      )
                    : resultRows;
                const dateAggregation =
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day';
                XYChartAxis.fillMissingTimeGapRows(
                    rows,
                    queryContext,
                    'date',
                    dateAggregation,
                    {
                        dimensionKeys: [
                            ...(decoSQL.splitGroup || splitsByPercentile
                                ? ['split_group']
                                : []),
                            ...(decoSQL.bucketGroup ? ['bucket_group'] : []),
                        ],
                    }
                );

                const data: FlowDashboardData = {
                    rows,
                    rangeAggregation: metric.rangeAggregation,
                };
                this.latestData = data;
                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
